import { debounce } from '../async.ts';
import { CustomError, checkPresent, doNothing } from '../core.ts';
import { isAbsent } from '../identity.ts';
import { filterIter } from '../iterators.ts';
import { Lru } from '../lru.ts';
import type { Scalar, Time } from '../units.ts';
import { Seconds } from '../units.ts';

export class HotIdError extends CustomError {
  constructor(id: string, op: 'read' | 'write', count: number, period: Scalar<Time>) {
    super(`Hotspot on id "${id}" which had ${count} ${op}s within ${String(period)}`, {
      id,
      op,
      count,
      period,
    });
  }
}

export interface HotSpotterOptions {
  // A resource `id` is "forgotten" after this duration elapses since its last access.
  period?: Scalar<Time>;
  // A resource `id` is "hot" if it is read more than this many times per `period`.
  maxReads?: number;
  // A resource `id` is "hot" if it is written more than this many times per `period`.
  maxWrites?: number;
  // The additional debouncing delay for issuing heat reports. The time to receive a report on hot
  // `id`s is `period` + `debounce`.
  debounce?: Scalar<Time>;
  // A resource `id` that reaches this level with throw HotIdError on read.
  exceptionalReads?: number;
  // A resource `id` that reaches this level with throw HotIdError on write.
  exceptionalWrites?: number;
  // Whether tracking is active. Used for easily disabling in production environments.
  // TODO: Consider whether exceptionalReads/Writes is important to protect prod resources.
  // TODO: Consider a do-nothing proxy wrapper.
  disableReports?: boolean;
  disableExceptions?: boolean;
}

interface HeatData {
  reads: number;
  writes: number;
}

const defaultPeriod = Seconds(3);
const defaultReportAge = Seconds(5);

// Helps to uncover frequent data access which may justify caching.
export class HotSpotter {
  readonly readEvent: (id: string) => void;
  readonly writeEvent: (id: string) => void;

  private readonly heat_: Lru<string, HeatData>;
  private readonly maxReads_: number = 2 * 20;
  private readonly maxWrites_: number = 1 * 20;
  private readonly exceptionalReads_ = Number.MAX_SAFE_INTEGER;
  private readonly exceptionalWrites_ = Number.MAX_SAFE_INTEGER;
  private readonly report_: () => void;

  // TODO: Consider also tracking the wildcard/any id, with much higher limits.
  constructor(options?: HotSpotterOptions) {
    const disableReports = options?.disableReports ?? false;
    const disableExceptions = options?.disableExceptions ?? false;
    const disableAll = disableReports && disableExceptions;

    this.maxReads_ = options?.maxReads ?? this.maxReads_;
    this.maxWrites_ = options?.maxWrites ?? this.maxWrites_;

    if (!disableExceptions) {
      this.exceptionalReads_ = options?.exceptionalReads ?? this.exceptionalReads_;
      this.exceptionalWrites_ = options?.exceptionalWrites ?? this.exceptionalWrites_;
    }

    const maxAge = options?.period ?? defaultPeriod;
    this.heat_ = new Lru<string, HeatData>({ maxAge });

    const delay = options?.debounce ?? defaultReportAge.minus(maxAge).atLeast(Seconds(0));
    this.report_ = disableReports
      ? doNothing
      : debounce(
          () => {
            this.doReport();
          },
          { delay },
        );

    this.readEvent = disableAll
      ? doNothing
      : (id) => {
          this.doReadEvent(id);
        };
    this.writeEvent = disableAll
      ? doNothing
      : (id) => {
          this.doWriteEvent(id);
        };
  }

  private doReadEvent(id: string) {
    const data = this.lookup(id);
    data.reads += 1;
    if (++data.reads > this.maxReads_) {
      if (data.reads > this.exceptionalReads_) {
        throw new HotIdError(id, 'read', data.reads, checkPresent(this.heat_.options?.maxAge));
      }
      this.report_();
    }
  }

  private doWriteEvent(id: string) {
    const data = this.lookup(id);
    data.reads = 0;
    if (++data.writes > this.maxWrites_) {
      if (data.writes > this.exceptionalWrites_) {
        throw new HotIdError(id, 'write', data.writes, checkPresent(this.heat_.options?.maxAge));
      }
      this.report_();
    }
  }

  private lookup(id: string) {
    let data = this.heat_.get(id);
    if (isAbsent(data)) {
      data = { reads: 0, writes: 0 };
      this.heat_.set(id, data);
    }
    return data;
  }

  private doReport() {
    const histogram = Array.from(
      filterIter(
        this.heat_.entries(),
        ([_, heat]) => heat.reads > this.maxReads_ || heat.writes > this.maxWrites_,
      ),
    );
    histogram.sort(([_, a], [__, b]) => b.reads + b.writes - (a.reads + a.writes));
    const period = checkPresent(this.heat_.options?.maxAge);
    console.log(
      `HotSpotter report: (period: ${String(period)}, format: <id>[<reads>,<writes>])\n`,
      ...histogram.map(([id, heat]) => `${id}[${heat.reads},${heat.writes}]`),
    );
  }
}
